import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Divider,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    Card,
    CardContent,
    Chip
} from '@mui/material';
import {
    AccountBalance as FeesIcon,
    Receipt as ReceiptIcon,
    Error as ErrorIcon,
    Info as InfoIcon,
    Payment as PaymentIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import FeesService from '../../../services/TestingFeesService';
import paymentService from '../../../services/TestingPaymentService';
import { LoaderWrapper } from '../../../components/index';
import { decryptText, decryptAppId } from '../../../utils/encryption';

const FeesSection = ({ data, onUpdate }) => {
    const { orgId: urlOrgId } = useParams();
    const location = useLocation();
    
    // Function to get orgId from URL query parameters with decryption
    const getOrgIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedOrgId = searchParams.get('orgId');
        if (encryptedOrgId) {
            try {
                const decryptedOrgId = decryptText(encryptedOrgId);
                console.log('📍 FeesSection - Decrypted orgId from URL query:', decryptedOrgId);
                console.log('📍 FeesSection - Original encrypted orgId:', encryptedOrgId);
                return decryptedOrgId;
            } catch (error) {
                console.log('⚠️ FeesSection - Failed to decrypt orgId, using original:', encryptedOrgId, error);
                return encryptedOrgId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get appId from URL query parameters with decryption
    const getAppIdFromQuery = useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        const encryptedAppId = searchParams.get('appId');
        if (encryptedAppId) {
            try {
                const decryptedAppId = decryptAppId(encryptedAppId);
                console.log('📍 FeesSection - Decrypted appId from URL query:', decryptedAppId);
                console.log('📍 FeesSection - Original encrypted appId:', encryptedAppId);
                return decryptedAppId;
            } catch (error) {
                console.log('⚠️ FeesSection - Failed to decrypt appId, using original:', encryptedAppId, error);
                return encryptedAppId; // Return original if decryption fails
            }
        }
        return null;
    }, [location.search]);

    // Function to get orgId with priority: URL query (encrypted) > URL params > null
    const getOrgId = useCallback(() => {
        // First check URL query parameters (encrypted) - highest priority
        const queryOrgId = getOrgIdFromQuery();
        if (queryOrgId) {
            console.log('📍 FeesSection - Using decrypted orgId from URL query:', queryOrgId);
            return queryOrgId;
        }

        // Then check URL parameters (unencrypted)
        if (urlOrgId) {
            console.log('📍 FeesSection - Using orgId from URL params:', urlOrgId);
            return urlOrgId;
        }

        // No fallback - orgId must be provided via URL
        console.log('⚠️ FeesSection - No orgId found in URL parameters');
        return null;
    }, [getOrgIdFromQuery, urlOrgId]);

    // Function to get appId with priority: URL query (encrypted) only
    const getAppId = useCallback(() => {
        // Only check URL query parameters (encrypted)
        const queryAppId = getAppIdFromQuery();
        if (queryAppId) {
            console.log('📍 FeesSection - Using decrypted appId from URL query:', queryAppId);
            return queryAppId;
        }

        // No fallback - appId must be provided via URL query parameters
        console.log('⚠️ FeesSection - No appId found in URL query parameters');
        return null;
    }, [getAppIdFromQuery]);

    // State management for fees data
    const [feesData, setFeesData] = useState({
        appId: null,
        feesBreakdown: [],
        summary: {
            grossTotal: 0,
            gstRate: 18,
            gstAmount: 0,
            grandTotal: 0
        },
        ...data
    });

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [applicationDetails, setApplicationDetails] = useState(null);
    const [paymentDetails, setPaymentDetails] = useState(null);
    const [paymentLoading, setPaymentLoading] = useState(false);

    // Note: Encrypted parameter functions are now defined above in the component

    // Load payment details
    const loadPaymentDetails = async (orgId, appId) => {
        setPaymentLoading(true);
        try {
            const response = await paymentService.checkPaymentStatus(orgId, appId);
            console.log('Payment status response:', response);
            
            if (response.success && response.data) {
                setPaymentDetails(response.data);
            } else {
                setPaymentDetails(null);
            }
        } catch (error) {
            console.error('Error loading payment details:', error);
            setPaymentDetails(null);
        } finally {
            setPaymentLoading(false);
        }
    };

    // Load fees data
    const loadFeesData = useCallback(async (appId) => {
        setLoading(true);
        setError(null);
        
        // Remove duplicate fees entries based on category and fee type
        const removeDuplicateFees = (feesArray) => {
            const seen = new Map();
            return feesArray.filter(item => {
                const key = `${item.category}_${item.FeeType}_${item.fees}`;
                if (seen.has(key)) {
                    return false;
                }
                seen.set(key, true);
                return true;
            });
        };
        
        try {
            // Load fees calculation and application details in parallel
            const [feesResponse, detailsResponse] = await Promise.all([
                FeesService.getFeesByAppId(appId),
                FeesService.getApplicationDetails(appId)
            ]);

            if (feesResponse.status === 'success') {
                // Remove duplicates from fees breakdown based on category and fee type
                const deduplicatedFees = removeDuplicateFees(feesResponse.data.feesBreakdown || []);
                
                setFeesData(prev => ({
                    ...prev,
                    feesBreakdown: deduplicatedFees,
                    summary: feesResponse.data.summary || prev.summary
                }));
            } else {
                throw new Error(feesResponse.message || 'Failed to load fees data');
            }

            if (detailsResponse.status === 'success') {
                setApplicationDetails(detailsResponse.data);
            }

        } catch (err) {
            console.error('Fees data Not Found:', err);
            setError(err.message || 'Failed to load fees data');
        } finally {
            setLoading(false);
        }
    }, []);

    // Load fees data on component mount
    useEffect(() => {
        const appId = getAppId();
        const orgId = getOrgId();
        if (appId && FeesService.isValidAppId(appId)) {
            setFeesData(prev => ({ ...prev, appId }));
            loadFeesData(appId);
            
            // Load payment details if orgId is available
            if (orgId) {
                loadPaymentDetails(orgId, appId);
            }
        } else {
            setError('No valid Application ID found. Please ensure you have started an application.');
        }
    }, [getAppId, getOrgId, loadFeesData]);

    // Update parent component when fees data changes
    useEffect(() => {
        if (onUpdate) {
            onUpdate(feesData);
        }
    }, [feesData, onUpdate]);

    const formatCurrency = (amount) => {
        return FeesService.formatCurrency(amount);
    };

    const groupedFees = FeesService.groupFeesByCategory(feesData.feesBreakdown);

    // Show loading state
    if (loading) {
        return (
            <LoaderWrapper 
                loading={true}
                type="page"
                message={`Loading fees data for Application ID: ${feesData.appId || '...'}`}
                size="medium"
            />
        );
    }

    // Show error state
    if (error) {
        return (
            <Box sx={{
                p: 3,
                backgroundColor: '#f8fafc',
                minHeight: '100vh'
            }}>
                <Alert 
                    severity="error" 
                    icon={<ErrorIcon />}
                    sx={{ mb: 3 }}
                    action={
                        <Typography 
                            variant="button" 
                            onClick={() => {
                                const appId = getAppId();
                                if (appId) loadFeesData(appId);
                            }}
                            sx={{ 
                                cursor: 'pointer', 
                                textDecoration: 'underline',
                                color: 'inherit'
                            }}
                        >
                            Retry
                        </Typography>
                    }
                >
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Error Loading Fees Data
                    </Typography>
                    <Typography>{error}</Typography>
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{
            p: 3,
            backgroundColor: '#f8fafc',
            minHeight: '100vh'
        }}>
            {/* Header Section */}
            {/* <Alert severity="info" sx={{ mb: 3 }}>
                <Typography component="div">
                    <strong>Note:</strong> Fees are calculated based on your laboratory category and number of collection centers. GST at {feesData.summary.gstRate}% is applicable on the gross total.
                </Typography>
            </Alert> */}

            {/* Fees Calculation Table */}
            <Paper sx={{
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                borderRadius: 2,
                overflow: 'hidden'
            }}>
                <Box sx={{
                    py: 2,
                    px: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    borderRadius: 1,
                    mb: 3,
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FeesIcon />
                        Total Fees
                    </Typography>
                </Box>

                <TableContainer>
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow sx={{ 
                                backgroundColor: '#fafbfc',
                                '& .MuiTableCell-head': {
                                    borderBottom: '2px solid #e2e8f0',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: '#475569',
                                    py: 2,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.025em'
                                }
                            }}>
                                <TableCell sx={{ width: '70%' }}>Category</TableCell>
                                <TableCell align="right" sx={{ width: '30%' }}>Fees</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Dynamic Fees Breakdown from API */}
                            {feesData.feesBreakdown.map((item, index) => (
                                <TableRow key={index} sx={{
                                    '&:hover': { backgroundColor: '#f8fafc' },
                                    '& .MuiTableCell-root': {
                                        borderBottom: '1px solid #e2e8f0',
                                        py: 2
                                    }
                                }}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                                            {item.category}
                                        </Typography>
                                        {item.FeeType && item.FeeType !== item.category && (
                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                {item.FeeType}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669' }}>
                                            {formatCurrency(item.fees)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {/* Show message if no fees data */}
                            {feesData.feesBreakdown.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 4 }}>
                                        <Typography variant="body1" color="text.secondary">
                                            No fees data available for this application.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* Gross Total */}
                            {feesData.summary.grossTotal > 0 && (
                                <TableRow sx={{
                                    backgroundColor: '#f1f5f9',
                                    '& .MuiTableCell-root': {
                                        borderBottom: '2px solid #d1d5db',
                                        py: 2
                                    }
                                }}>
                                    <TableCell>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1f2937' }}>
                                            Gross Total
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#1f2937' }}>
                                            {formatCurrency(feesData.summary.grossTotal)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* GST */}
                            {feesData.summary.gstAmount > 0 && (
                                <TableRow sx={{
                                    '&:hover': { backgroundColor: '#f8fafc' },
                                    '& .MuiTableCell-root': {
                                        borderBottom: '1px solid #e2e8f0',
                                        py: 2
                                    }
                                }}>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#334155' }}>
                                            GST ({feesData.summary.gstRate}%)
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#dc2626' }}>
                                            {formatCurrency(feesData.summary.gstAmount)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {/* Grand Total */}
                            {feesData.summary.grandTotal > 0 && (
                                <TableRow sx={{
                                    backgroundColor: '#667eea',
                                    '& .MuiTableCell-root': {
                                        borderBottom: 'none',
                                        py: 3,
                                        color: 'white'
                                    }
                                }}>
                                    <TableCell>
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                                            Grand Total
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
                                            {formatCurrency(feesData.summary.grandTotal)}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Fee Structure Summary */}
            {feesData.summary.grossTotal > 0 && (
                <Paper sx={{
                    mt: 3,
                    p: 3,
                    backgroundColor: '#f0f9ff',
                    border: '1px solid #0ea5e9',
                    borderRadius: 2
                }}>
                    <Typography variant="h6" sx={{ mb: 2, color: '#0369a1', fontWeight: 600 }}>
                        Fee Structure Summary
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                                    GROSS TOTAL
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#059669', fontWeight: 700 }}>
                                    {formatCurrency(feesData.summary.grossTotal)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                                    GST RATE
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#0ea5e9', fontWeight: 700 }}>
                                    {feesData.summary.gstRate}%
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: 'white', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.75rem' }}>
                                    GST AMOUNT
                                </Typography>
                                <Typography variant="h6" sx={{ color: '#dc2626', fontWeight: 700 }}>
                                    {formatCurrency(feesData.summary.gstAmount)}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#667eea', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ color: 'white', fontSize: '0.75rem' }}>
                                    GRAND TOTAL
                                </Typography>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                                    {formatCurrency(feesData.summary.grandTotal)}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>
            )}
        
            {/* Payment Details Section */}
            {!paymentLoading && paymentDetails && (
                <Paper sx={{
                    mt: 3,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    borderRadius: 2,
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        py: 2,
                        px: 2,
                        background: paymentDetails.order_status?.toLowerCase() === 'success' 
                            ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                            : paymentDetails.order_status?.toLowerCase() === 'failed'
                            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                            : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: 'white',
                        borderRadius: 1,
                        mb: 0,
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <PaymentIcon />
                            Payment Details
                            <Chip 
                                label={paymentDetails.order_status || 'Pending'}
                                size="small"
                                icon={
                                    paymentDetails.order_status?.toLowerCase() === 'success' ? <CheckCircleIcon /> :
                                    paymentDetails.order_status?.toLowerCase() === 'failed' ? <CancelIcon /> :
                                    <AccessTimeIcon />
                                }
                                sx={{ 
                                    ml: 'auto',
                                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                                    color: 'white',
                                    fontWeight: 'bold',
                                    '& .MuiChip-icon': {
                                        color: 'white'
                                    }
                                }}
                            />
                        </Typography>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            {/* Order ID */}
                            <Grid item xs={12} sm={6} md={4}>
                                <Card variant="outlined" sx={{ height: '100%', bgcolor: '#f8fafc' }}>
                                    <CardContent>
                                        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                                            Order ID
                                        </Typography>
                                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mt: 1 }}>
                                            {paymentDetails.order_id || 'N/A'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Transaction ID */}
                            {paymentDetails.transaction_id && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card variant="outlined" sx={{ height: '100%', bgcolor: '#f8fafc' }}>
                                        <CardContent>
                                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                                                Transaction ID
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mt: 1 }}>
                                                {paymentDetails.transaction_id}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Amount */}
                            <Grid item xs={12} sm={6} md={4}>
                                <Card variant="outlined" sx={{ height: '100%', bgcolor: '#f8fafc' }}>
                                    <CardContent>
                                        <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                                            Amount Paid
                                        </Typography>
                                        <Typography variant="h6" sx={{ 
                                            fontWeight: 700, 
                                            color: paymentDetails.order_status?.toLowerCase() === 'success' ? '#059669' : '#1e293b',
                                            mt: 1 
                                        }}>
                                            {paymentDetails.amount ? formatCurrency(paymentDetails.amount) : 'N/A'}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Payment Method */}
                            {paymentDetails.payment_method && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card variant="outlined" sx={{ height: '100%', bgcolor: '#f8fafc' }}>
                                        <CardContent>
                                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                                                Payment Method
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mt: 1 }}>
                                                {paymentDetails.payment_method}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Payment Date */}
                            {paymentDetails.payment_date && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card variant="outlined" sx={{ height: '100%', bgcolor: '#f8fafc' }}>
                                        <CardContent>
                                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                                                Payment Date
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mt: 1 }}>
                                                {new Date(paymentDetails.payment_date).toLocaleString('en-IN', {
                                                    year: 'numeric',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Bank Reference Number */}
                            {paymentDetails.bank_ref_no && (
                                <Grid item xs={12} sm={6} md={4}>
                                    <Card variant="outlined" sx={{ height: '100%', bgcolor: '#f8fafc' }}>
                                        <CardContent>
                                            <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', fontWeight: 600 }}>
                                                Bank Reference No
                                            </Typography>
                                            <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b', mt: 1 }}>
                                                {paymentDetails.bank_ref_no}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            )}

                            {/* Status Message */}
                            {paymentDetails.status_message && (
                                <Grid item xs={12}>
                                    <Alert 
                                        severity={
                                            paymentDetails.order_status?.toLowerCase() === 'success' ? 'success' :
                                            paymentDetails.order_status?.toLowerCase() === 'failed' ? 'error' :
                                            'info'
                                        }
                                        icon={
                                            paymentDetails.order_status?.toLowerCase() === 'success' ? <CheckCircleIcon /> :
                                            paymentDetails.order_status?.toLowerCase() === 'failed' ? <CancelIcon /> :
                                            <InfoIcon />
                                        }
                                    >
                                        <Typography variant="body2">
                                            <strong>Status:</strong> {paymentDetails.status_message}
                                        </Typography>
                                    </Alert>
                                </Grid>
                            )}
                        </Grid>
                    </Box>
                </Paper>
            )}

            {/* No Payment Details Message */}
            {!paymentLoading && !paymentDetails && feesData.summary.grandTotal > 0 && (
                <Paper sx={{
                    mt: 3,
                    p: 3,
                    backgroundColor: '#fff7ed',
                    border: '1px solid #fb923c',
                    borderRadius: 2
                }}>
                    <Alert severity="warning" icon={<InfoIcon />}>
                        <Typography variant="body1">
                            No payment has been made for this application yet. Please proceed to the Declaration section to complete the payment.
                        </Typography>
                    </Alert>
                </Paper>
            )}

        </Box>
    );
};

export default FeesSection;
